import React, { useEffect } from 'react'
import s from './styles/StepTimeline.module.scss'
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg'
import { ReactComponent as ClockIcon } from 'assets/icons/clock.svg'
import { ReactComponent as ClockFullIcon } from 'assets/icons/clock-full.svg'
import { useStepTimeline } from './hooks/useStepTimeline'
import DatePickerInput from 'shared/components/DatePickerInput/DatePickerInput'
import TimeInput from 'shared/components/TimeInput/TimeInput'

const formatSummaryDate = dt => {
	if (!dt) return '—'
	return dt.toLocaleDateString(undefined, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	})
}

const formatSummaryTime = dt => {
	if (!dt) return '—'
	return dt.toLocaleTimeString(undefined, {
		hour: 'numeric',
		minute: '2-digit',
	})
}

const StepTimeline = ({
	onValidityChange,
	onTimelineDataChange,
	initialData = {},
}) => {
	const {
		openDate,
		openTime,
		closeDate,
		closeTime,
		setOpenDate,
		setOpenTime,
		setCloseDate,
		setCloseTime,
		openDt,
			closeDt,
			closePresetOptions,
			activePresetDays,
			applyClosePreset,
			durationValueText,
		} = useStepTimeline(onValidityChange, initialData)

	// Save timeline data to parent when it changes
	useEffect(() => {
		if (typeof onTimelineDataChange === 'function') {
			onTimelineDataChange({
				openDate,
				openTime,
				closeDate,
				closeTime,
				openDt,
				closeDt,
			})
		}
	}, [
		openDate,
		openTime,
		closeDate,
		closeTime,
		openDt,
		closeDt,
		onTimelineDataChange,
	])

	return (
		<div className={s.timeline}>
			<header className={s.timeline__header}>
				<h2 className={s.timeline__title}>
					When is this assignment available?
				</h2>
				<p className={s.timeline__subtitle}>
					Set when students can start and when submissions should close.
				</p>
			</header>

			<div className={s.timeline__content}>
				<div className={s.timeline__grid}>
					<section className={s.card}>
						<div className={s.card__head}>
							<span className={s.card__icon}>
								<CalendarIcon />
							</span>
							<div>
								<div className={s.card__title}>Opens</div>
								<div className={s.card__hint}>Pre-filled to today. Change if needed.</div>
							</div>
						</div>

						<div className={s.card__fieldGroup}>
							<label className={s.field}>
								<span className={s.field__label}>Date</span>
								<DatePickerInput
									value={openDate}
									onChange={setOpenDate}
									className={s.dateInput}
									inputClassName={s.field__input}
								/>
							</label>
							<label className={s.field}>
								<span className={s.field__label}>Time</span>
								<TimeInput
									value={openTime}
									onChange={setOpenTime}
									className={s.timeInput}
									inputClassName={s.field__input}
								/>
							</label>
						</div>
					</section>

					<section className={s.card}>
						<div className={s.card__head}>
							<span className={s.card__icon}>
								<CalendarIcon />
							</span>
							<div>
								<div className={s.card__title}>Closes</div>
								<div className={s.card__hint}>Choose a deadline or use a quick duration.</div>
							</div>
						</div>

						<div className={s.card__fieldGroup}>
							<label className={s.field}>
								<span className={s.field__label}>Date</span>
								<DatePickerInput
									value={closeDate}
									onChange={setCloseDate}
									minISO={openDate}
									className={s.dateInput}
									inputClassName={s.field__input}
								/>
							</label>
							<label className={s.field}>
								<span className={s.field__label}>Time</span>
								<TimeInput
									value={closeTime}
									onChange={setCloseTime}
									className={s.timeInput}
									inputClassName={s.field__input}
								/>
							</label>
						</div>

						<div className={s.presets} aria-label="Quick close date options">
							{closePresetOptions.map(option => {
								const isActive = activePresetDays === option.days
								return (
									<button
										key={option.days}
										type="button"
										className={`${s.preset} ${isActive ? s.preset_active : ''}`}
										onClick={() => applyClosePreset(option.days)}
										aria-pressed={isActive}
									>
										<CalendarIcon />
										<span className={s.preset__label}>{option.label}</span>
									</button>
								)
							})}
						</div>
					</section>
				</div>

				<section className={s.summary}>
					<div className={s.summary__head}>
						<span className={s.summary__icon}>
							<ClockIcon />
						</span>
						<div className={s.summary__title}>Timeline Summary</div>
					</div>
						<div className={s.summary__grid}>
							<div className={s.summary__item}>
								<span className={s.summary__label}>Opens</span>
								<div className={s.summary__valueRow}>
									<span className={s.summary__valueMeta}>
										<span className={s.summary__valueIcon}>
											<CalendarIcon />
										</span>
										<span className={s.summary__value}>{formatSummaryDate(openDt)}</span>
									</span>
									<span className={s.summary__valueMeta}>
										<span className={s.summary__valueIcon}>
											<ClockFullIcon />
										</span>
										<span className={s.summary__value}>{formatSummaryTime(openDt)}</span>
									</span>
								</div>
							</div>
							<div className={s.summary__item}>
								<span className={s.summary__label}>Closes</span>
								<div className={s.summary__valueRow}>
									<span className={s.summary__valueMeta}>
										<span className={s.summary__valueIcon}>
											<CalendarIcon />
										</span>
										<span className={s.summary__value}>{formatSummaryDate(closeDt)}</span>
									</span>
									<span className={s.summary__valueMeta}>
										<span className={s.summary__valueIcon}>
											<ClockFullIcon />
										</span>
										<span className={s.summary__value}>{formatSummaryTime(closeDt)}</span>
									</span>
								</div>
							</div>
						<div className={`${s.summary__item} ${s.summary__item_duration}`}>
							<span className={s.summary__label}>Duration</span>
							<span className={s.summary__value}>{durationValueText}</span>
						</div>
					</div>
				</section>
			</div>
		</div>
	)
}

export default StepTimeline
